import React, { cloneElement } from 'react';
import { useTranslation } from 'react-i18next';

import { TSyncStatusProps } from '@/console/features/devices/components/sync-status/sync-status.types';
import { SyncStatusIcon } from '@/console/features/groups/components/group-sync-status/group-sync-status.styled';
import { JobLogsDrawer } from '@/console/features/jobs/components/job-logs-drawer/lazy-job-logs-drawer';
import { Chip } from '@/shared/components/ui/chip/chip';
import { ChipStatus } from '@/shared/components/ui/chip/chip.types';
import { useJobStatus } from '@/shared/features/jobs/hooks/use-job-status/use-job-status';
import { DeviceSyncStatusEnum } from '@/shared/types/types.generated';

export const SyncStatus: React.FC<TSyncStatusProps> = ({
  status,
  jobStatus,
  icon,
  tooltip,
  syncJobId,
  syncStatusCount = 0,
  defaultTab,
  onClick,
  deferJobFetchUntilOpen,
}) => {
  const { t } = useTranslation();
  const { getStatusColor, getStatusLabel, isJobRunning } = useJobStatus();
  const isRunning = !!jobStatus && isJobRunning(jobStatus);

  const getChipColor = (): ChipStatus => {
    if (isRunning) {
      return getStatusColor(jobStatus!);
    }

    switch (status) {
      case DeviceSyncStatusEnum.Error:
        return 'red';
      case DeviceSyncStatusEnum.Synced:
        return 'green';
      case DeviceSyncStatusEnum.NotSynced:
        return 'yellow';
      default:
        return 'grey';
    }
  };

  const getChipText = () => {
    if (icon) {
      return <SyncStatusIcon />;
    }

    if (isRunning) {
      return getStatusLabel(jobStatus!);
    }

    if (status) {
      return t(`projects:sync_status_enum.${status.toLowerCase()}`);
    }

    return t('projects:sync_status_enum.unknown');
  };

  const renderText = () => {
    const chipText = getChipText();

    if (syncStatusCount > 1) {
      return `${chipText} (${syncStatusCount})`;
    }

    return chipText;
  };

  const renderChip = () => {
    return (
      <Chip
        withDot={isRunning}
        variant="status"
        status={getChipColor()}
        label={renderText()}
        tooltip={tooltip}
        tooltipProps={{ noMaxWidth: true, placement: 'right' }}
      />
    );
  };

  if (syncJobId) {
    return (
      <JobLogsDrawer
        jobId={syncJobId}
        trigger={renderChip()}
        defaultTab={defaultTab}
        deferJobFetchUntilOpen={deferJobFetchUntilOpen}
      />
    );
  }

  return cloneElement(renderChip(), { onClick });
};
